import { Card } from "../components/Card";
import { Skeleton } from "../components/Skeleton";
import { formatMoney } from "../lib/format";
import styles from "./CreditPanel.module.css";
import type { SellerCredit, SignupGrant } from "../api/types";

/**
 * The seller's promotional credit position.
 *
 * Three figures, and they are core's (canon CORE-007). `docs/SECURITY.md` § Credits/bids
 * makes this display-only: nothing here adds, subtracts or projects a balance, because the
 * server recomputes the same sums when it accepts a bid, and a number this app calculated
 * would be a second opinion about money.
 *
 * `available` leads because it is the only one of the three a founder can act on. The other
 * two are shown next to it rather than hidden, since "where did my credit go" is exactly the
 * question a reserved balance raises — canon § 5 answers it: a standing bid holds credit,
 * and a losing bid gives it back.
 *
 * The non-cash sentence is rendered from `terms.message`, verbatim as core sent it. Canon
 * `AGENTS.md` § 7 forbids describing promotional credit as withdrawable money, and
 * `contracts/API.md` ships that sentence with the numbers so no surface can quietly drop it.
 */

/** Only the three integer-cent fields; the currency and the terms are not figures. */
type CreditFigure = "availableCents" | "reservedCents" | "consumedCents";

const FIGURES: { key: CreditFigure; label: string; note: string }[] = [
  {
    key: "availableCents",
    label: "Available",
    note: "Ready to back a bid for tomorrow's Top 10.",
  },
  {
    key: "reservedCents",
    label: "Reserved",
    note: "Held by a bid that is still standing. It returns to available if that bid does not win.",
  },
  {
    key: "consumedCents",
    label: "Consumed",
    note: "Already spent on placements you won.",
  },
];

export function CreditSkeleton() {
  return (
    <Card className={styles.panel}>
      <div className={styles.figures}>
        {FIGURES.map((figure) => (
          <div key={figure.key} className={styles.figure}>
            <Skeleton width="5rem" height="0.75rem" />
            <Skeleton width="7rem" height="2rem" />
          </div>
        ))}
      </div>
    </Card>
  );
}

/**
 * What a zero balance means for *this* founder (canon § 5, decision `D-011`).
 *
 * Three different facts wear the same $0.00, and only the first is a waiting state:
 *
 *   - no deal approved yet — the grant has not happened and money is still coming;
 *   - approved into canon § 4's Founding cohort — the grant *did* happen and was worth
 *     nothing, because canon § 6 gave them a free board position instead. Repeating the
 *     "credit arrives at approval" line here would promise the ten earliest founders money
 *     that is never arriving;
 *   - granted and then fully spent — nothing is owed and nothing is pending.
 */
function emptyMessage(grant: SignupGrant): string {
  if (grant.band === null) {
    return "Dealbid grants your signup promotional credit once your first deal is approved.";
  }
  if (grant.band === "founding") {
    return (
      "You are a Founding Deal. Your place on the board is free while Dealbid is still " +
      "filling it, so there was no signup credit to grant."
    );
  }
  return "Your signup promotional credit has all been committed.";
}

export function CreditPanel({ credit }: { credit: SellerCredit }) {
  const isEmpty =
    credit.availableCents === 0 && credit.reservedCents === 0 && credit.consumedCents === 0;

  return (
    <Card className={styles.panel}>
      <dl className={styles.figures}>
        {FIGURES.map((figure, index) => (
          <div
            key={figure.key}
            className={[styles.figure, index === 0 ? styles.lead : null]
              .filter(Boolean)
              .join(" ")}
          >
            <dt className={styles.label}>{figure.label}</dt>
            <dd className={styles.value}>
              <span className={`${styles.amount} figure`}>
                {formatMoney(credit[figure.key])}
              </span>
              <span className={styles.note}>{figure.note}</span>
            </dd>
          </div>
        ))}
      </dl>

      {isEmpty ? <p className={styles.empty}>{emptyMessage(credit.signupGrant)}</p> : null}

      <p className={styles.terms}>{credit.terms.message}</p>
    </Card>
  );
}
